import React from "react";
import { Box, Text } from "@chakra-ui/react";
import { motion, useReducedMotion } from "framer-motion";
import { alpha, useGovTheme } from "./useGovTheme";

/**
 * A ring that fills to `value` of `max`. Optional `marker` draws a tick at a threshold (e.g. quorum), so
 * "how far to go" is visible at a glance. The centre label is the real number.
 */
export function GovProgressRing({ value, max, size = 72, stroke = 7, label, sub, marker, tone }: {
  value: number; max: number; size?: number; stroke?: number; label?: React.ReactNode; sub?: string; marker?: number; tone?: string;
}) {
  const t = useGovTheme();
  const reduce = useReducedMotion();
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  const color = tone || (pct >= 1 ? t.success : t.primary);
  const markerAngle = marker !== undefined && max > 0 ? (Math.min(1, marker / max) * 360 - 90) * (Math.PI / 180) : null;
  return (
    <Box position="relative" w={`${size}px`} h={`${size}px`} flexShrink={0} role="img" aria-label={`${value} of ${max}${sub ? ` — ${sub}` : ""}`}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={alpha(color, 0.15)} strokeWidth={stroke} />
        <motion.circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} transform={`rotate(-90 ${size / 2} ${size / 2})`}
          initial={{ strokeDashoffset: reduce ? c * (1 - pct) : c }} animate={{ strokeDashoffset: c * (1 - pct) }}
          transition={{ duration: reduce ? 0 : 1, ease: [0.22, 1, 0.36, 1] }} />
        {markerAngle !== null && (
          <line x1={size / 2 + (r - stroke) * Math.cos(markerAngle)} y1={size / 2 + (r - stroke) * Math.sin(markerAngle)}
            x2={size / 2 + (r + stroke) * Math.cos(markerAngle)} y2={size / 2 + (r + stroke) * Math.sin(markerAngle)}
            stroke={t.warning} strokeWidth={2.5} strokeLinecap="round" />
        )}
      </svg>
      <Box position="absolute" inset={0} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
        {label !== undefined && <Text fontWeight="bold" fontSize={size >= 72 ? "lg" : "md"} lineHeight="1" color={t.text}>{label}</Text>}
        {sub && <Text fontSize="sm" lineHeight="1.1" color={t.muted} mt={1} fontWeight="semibold" textAlign="center" px={2}>{sub}</Text>}
      </Box>
    </Box>
  );
}
